import {getFormConfigErrors, getRequiredFormAnswerErrors} from "./formValidationHelpers.js"

function getWeeklyDiaryConfigErrors(config = {}) {
	if (!config.title || !config.entryLabel || !Number(config.weekLength)) {
		return ["Invalid weekly tracker configuration"]
	}
	for (const [key, label] of [["targetBehaviors", "target behavior"], ["contextFields", "context field"], ["skills", "skill"]]) {
		const errors = getDiaryItemListErrors(config[key] || [], label)
		if (errors.length) return errors
	}
	for (let section of config.ratingSections || []) {
		if (!section.id || !section.title || !Array.isArray(section.items)) {
			return ["Invalid weekly tracker rating section"]
		}
		const errors = getDiaryItemListErrors(section.items, "rating")
		if (errors.length) return errors
	}
	return []
}

function getDiaryItemListErrors(items = [], itemName = "item") {
	if (!Array.isArray(items)) {
		return [`Invalid ${itemName} configuration`]
	}
	for (let item of items) {
		if (!item.id || !item.title) {
			return [`Invalid ${itemName} configuration`]
		}
	}
	return []
}

function getWeeklyDiaryPayloadErrors(enginePayload = {}, config = {}) {
	let tracker = enginePayload.tracker || config || {}
	let validDayIds = new Set((tracker.days || []).map(day => day.id))
	for (let entry of enginePayload.entries || []) {
		if (!entry.dayId || (validDayIds.size && !validDayIds.has(entry.dayId)) || !entry.answers) {
			return ["Invalid weekly tracker entry"]
		}
		const errors = getWeeklyDiaryRatingErrors(entry, tracker)
		if (errors.length) return errors
	}
	return []
}

function getWeeklyDiaryRatingErrors(entry = {}, tracker = {}) {
	let ratingIds = new Set()
	for (let section of tracker.ratingSections || []) {
		for (let item of section.items || []) {
			ratingIds.add(item.id)
		}
	}
	let sessionEntryRatingIds = new Set((tracker.sessionEntryRatings || []).map(item => item.id))
	let ratingScaleMin = 1
	let ratingScaleMax = Number(tracker.ratingScaleMax ?? 5)
	const ratingErrors = getWeeklyDiaryRatingSetErrors(entry.answers?.ratings || {}, ratingIds, ratingScaleMin, ratingScaleMax)
	if (ratingErrors.length) return ratingErrors
	return getWeeklyDiaryRatingSetErrors(entry.answers?.sessionEntryRatings || {}, sessionEntryRatingIds, ratingScaleMin, ratingScaleMax)
}

function getWeeklyDiaryRatingSetErrors(ratingValues = {}, ratingIds = new Set(), ratingScaleMin = 0, ratingScaleMax = 5) {
	for (let [ratingId, value] of Object.entries(ratingValues)) {
		if (!ratingIds.has(ratingId) || value === "" || value == null) {
			continue
		}
		let rating = Number(value)
		if (Number.isNaN(rating) || rating < ratingScaleMin || rating > ratingScaleMax) {
			return ["Invalid weekly tracker rating"]
		}
	}
	return []
}

export function getTrackerConfigErrors(config = {}) {
	if (config.trackerKind === "weeklyDiary") return getWeeklyDiaryConfigErrors(config)
	if (!config.title || !config.entryLabel) return ["Invalid tracker tool configuration"]
	return getFormConfigErrors(config.entryForm || {})
}

export function getTrackerPayloadErrors(payload = {}, config = {}) {
	const tracker = payload.tracker || null
	const entries = payload.entries || []
	if (!tracker || !Array.isArray(entries) || !entries.length) return ["Invalid tracker engine payload"]
	const configErrors = getTrackerConfigErrors(tracker)
	if (configErrors.length) return configErrors
	if (tracker.trackerKind === "weeklyDiary") return getWeeklyDiaryPayloadErrors(payload, config)
	const entry = entries[0]
	const formErrors = getFormConfigErrors(entry.form || {})
	if (formErrors.length) return formErrors
	return getRequiredFormAnswerErrors(entry, config.entryForm || {})
}

export function createTrackerSummaryInput(payload = {}, config = {}) {
	const tracker = JSON.parse(JSON.stringify(payload.tracker || config || {}))
	const entries = JSON.parse(JSON.stringify(payload.entries || []))
	if (tracker.trackerKind === "weeklyDiary") return createWeeklyDiarySummaryInput(tracker, entries)
	const entry = entries[0] || {}
	return {
		headline: createTrackerHeadline(entry, tracker),
		tracker,
		entries,
		primaryMeasure: createTrackerPrimaryMeasure(entry, tracker),
	}
}

function createWeeklyDiarySummaryInput(tracker = {}, entries = []) {
	return {
		headline: tracker.periodAnchorLabel || tracker.periodRangeLabel || "",
		tracker,
		entries,
		ratingSummary: createWeeklyDiaryRatingSummary(tracker, entries),
		behaviorSummary: createWeeklyDiaryBehaviorSummary(tracker, entries),
		skillsSummary: createWeeklyDiarySkillsSummary(tracker, entries),
	}
}

function createWeeklyDiaryRatingSummary(tracker = {}, entries = []) {
	let result = {}
	for (let section of tracker.ratingSections || []) {
		for (let item of section.items || []) {
			let values = entries
				.map(entry => entry.answers?.ratings?.[item.id])
				.filter(value => value !== "" && value != null)
				.map(Number)
			if (values.length) {
				result[item.id] = {
					title: item.title,
					max: Math.max(...values),
					average: Math.round(values.reduce((sum, value) => sum + value, 0) / values.length * 10) / 10,
				}
			}
		}
	}
	return result
}

function createWeeklyDiaryBehaviorSummary(tracker = {}, entries = []) {
	let result = {}
	for (let behavior of tracker.targetBehaviors || []) {
		let values = entries.map(entry => entry.answers?.targetBehaviors?.[behavior.id]).filter(value => value !== "" && value != null)
		result[behavior.id] = {
			title: behavior.title,
			total: values.reduce((sum, value) => sum + (value === true ? 1 : Number(value) || 0), 0),
			reportedDays: values.length,
		}
	}
	return result
}

function createWeeklyDiarySkillsSummary(tracker = {}, entries = []) {
	let result = {}
	for (let skill of tracker.skills || []) {
		result[skill.id] = {
			title: skill.title,
			daysPracticed: entries.filter(entry => entry.answers?.skills?.[skill.id]).length,
		}
	}
	return result
}

function createTrackerHeadline(entry = {}, tracker = {}) {
	let result = tracker.entryLabel || "Entry"
	let primaryMeasure = createTrackerPrimaryMeasure(entry, tracker)
	if (primaryMeasure.value != null) {
		result = `${primaryMeasure.label}: ${primaryMeasure.value}`
	}
	return result
}

function createTrackerPrimaryMeasure(entry = {}, tracker = {}) {
	let primaryMeasureElementId = tracker.primaryMeasureElementId || ""
	let formElements = entry.form?.fields || entry.form?.formElements || []
	let primaryElement = formElements.find(formElement => formElement.id === primaryMeasureElementId) || null
	return {
		id: primaryMeasureElementId,
		label: primaryElement?.title || "",
		value: entry.answers?.[primaryMeasureElementId] ?? null,
	}
}


// Structural checks run before normalizers can discard malformed rows or values.
export function validateTrackerWorkInput(payload = {}, config = {}) {
	const isRecord = value => value !== null && typeof value === "object" && !Array.isArray(value)
	const assertRows = (value, name) => {
		if (value !== undefined && (!Array.isArray(value) || value.some(row => !isRecord(row)))) throw new Error(`Invalid tracker ${name}`)
	}
	if (!isRecord(payload)) throw new Error("Invalid tracker payload")
	if (config.trackerKind === "sudsSeries") {
		assertRows(payload.series, "series")
		for (const series of payload.series || []) assertRows(series.ratings, "ratings")
	} else {
		assertRows(payload.entries, "entries")
		assertRows(payload.archivedEntries, "archived entries")
		for (const entry of [...(payload.entries || []), ...(payload.archivedEntries || [])]) {
			if (entry.answers !== undefined && !isRecord(entry.answers)) throw new Error("Invalid tracker answers")
		}
		if (payload.session !== undefined && !isRecord(payload.session)) throw new Error("Invalid tracker session")
	}
}
